import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, TriangleAlert, ShieldCheck, Mail } from "lucide-react";
import { api, apiError, num, shortDate } from "@/lib/api";
import { AppShell, PageHeader, Panel, SkeletonRows, EmptyState, Btn, Tag } from "@/components/Shell";
import { SwapPrompt } from "@/components/SwapPrompt";

const causeLabel = (cause) => {
  if (!cause) return "—";
  if (cause.startsWith("component_shortage")) return `Component short: ${cause.split(":")[1]}`;
  return cause.replace(/_/g, " ");
};

export default function Gaps() {
  const [gaps, setGaps] = useState(null);
  const [digest, setDigest] = useState(null);
  const [history, setHistory] = useState([]);
  const [learned, setLearned] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [g, d, h, l] = await Promise.all([
      api.get("/gaps"),
      api.get("/gaps/digest"),
      api.get("/gaps/history", { params: { limit: 30 } }),
      api.get("/substitutions/learned"),
    ]);
    setGaps(g.data);
    setDigest(d.data);
    setHistory(h.data);
    setLearned(l.data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
  }, []);

  const raisePO = async (gap) => {
    try {
      const qty = Math.max(gap.shortfall * 2, gap.reorder_point || gap.shortfall);
      await api.post("/purchase-orders", {
        supplier_name: "Shenzhen Ming Trading",
        currency: "GBP",
        fx_rate: 1,
        eta_days: 21,
        lines: [{ product_id: gap.product_id, quantity: qty, unit_cost: 10 }],
      });
      toast.success(`PO raised for ${qty} × ${gap.sku}`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <AppShell>
      <PageHeader
        title="Gap engine"
        subtitle="Every shortfall with its cause, inbound ETA and ratio-adjusted substitute cover. Recalculated on every stock event."
        testId="gaps-header"
        actions={
          <Btn
            variant="outline"
            data-testid="recalculate-gaps-button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post("/gaps/recalculate");
                await load();
                toast.success("Gaps recalculated");
              } catch (e) {
                toast.error(apiError(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            <RefreshCw className="h-4 w-4" /> Recalculate
          </Btn>
        }
      />

      {digest && (
        <div className="mb-4 grid gap-4 sm:grid-cols-3" data-testid="gap-digest">
          <div className="border border-slate-200 bg-white p-4">
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Shortfalls</p>
            <p className="num mt-1 text-2xl font-medium text-red-600" data-testid="digest-total">{num(digest.total_gaps)}</p>
          </div>
          <div className="border border-slate-200 bg-white p-4">
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Covered by substitutes</p>
            <p className="num mt-1 text-2xl font-medium text-emerald-600" data-testid="digest-covered">{num(digest.covered_by_substitutes)}</p>
          </div>
          <div className="border border-slate-200 bg-white p-4">
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Uncovered — needs a PO</p>
            <p className="num mt-1 text-2xl font-medium text-slate-900" data-testid="digest-uncovered">{num(digest.uncovered)}</p>
          </div>
        </div>
      )}

      <Panel
        title="Shortfalls"
        testId="gaps-panel"
        right={<span className="flex items-center gap-1 text-xs text-slate-500"><Mail className="h-3.5 w-3.5" /> Daily digest at 07:00</span>}
      >
        {!gaps ? (
          <SkeletonRows rows={6} />
        ) : gaps.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            title="No gaps — everything is covered"
            body="Nothing is oversold or below its reorder point right now."
            testId="gaps-empty"
          />
        ) : (
          <div className="divide-y divide-slate-100">
            {gaps.map((g) => (
              <div key={g.product_id} className="p-4" data-testid={`gap-row-${g.sku}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Link to={`/app/products/${g.product_id}`} className="mono text-sm hover:underline">{g.sku}</Link>
                      {g.is_bundle && <Tag tone="violet">Bundle</Tag>}
                      <Tag tone={g.covered_by_alternatives ? "green" : "red"}>
                        {g.covered_by_alternatives ? "Substitute cover" : "Uncovered"}
                      </Tag>
                    </div>
                    <p className="mt-1 text-sm text-slate-700">{g.name}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-5 text-right">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-500">Shortfall</p>
                      <p className="num text-lg text-red-600" data-testid={`gap-shortfall-${g.sku}`}>{num(g.shortfall)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-500">Available</p>
                      <p className="num text-lg" style={{ color: g.available < 0 ? "#EF4444" : "#10B981" }}>{num(g.available)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-500">Incoming</p>
                      <p className="num text-lg text-violet-600">{num(g.incoming)}</p>
                      <p className="text-[10px] text-slate-500">ETA {shortDate(g.incoming_eta)}</p>
                    </div>
                    <Btn variant="outline" data-testid={`raise-po-${g.sku}`} onClick={() => raisePO(g)}>Raise PO</Btn>
                  </div>
                </div>

                <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
                  <TriangleAlert className="h-3.5 w-3.5 text-amber-500" /> Cause: {causeLabel(g.cause)}
                  {g.is_bundle && g.buildable !== undefined && ` · buildable now: ${g.buildable}`}
                </p>

                <div className="mt-3">
                  <SwapPrompt productId={g.product_id} sku={g.sku} shortfall={g.shortfall} onLearned={load} />
                </div>

                {g.alternatives?.length > 0 && (
                  <div className="mt-3 border border-slate-200">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Substitute</th>
                          <th className="px-3 py-1.5 text-right">Ratio</th>
                          <th className="px-3 py-1.5 text-right">Priority</th>
                          <th className="px-3 py-1.5 text-right">Available</th>
                          <th className="px-3 py-1.5 text-right">Ratio-adjusted cover</th>
                          <th className="px-3 py-1.5 text-right">Learned</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {g.alternatives.map((a) => (
                          <tr key={a.product_id} data-testid={`gap-${g.sku}-alt-${a.sku}`}>
                            <td className="px-3 py-1.5">
                              <Link to={`/app/products/${a.product_id}`} className="mono hover:underline">{a.sku}</Link>
                              <span className="ml-2 text-xs text-slate-500">{a.name}</span>
                            </td>
                            <td className="num px-3 py-1.5 text-right">{a.ratio}</td>
                            <td className="num px-3 py-1.5 text-right">{a.priority}</td>
                            <td className="num px-3 py-1.5 text-right">{num(a.available)}</td>
                            <td className="num px-3 py-1.5 text-right font-medium text-emerald-600">{num(a.adjusted_cover)}</td>
                            <td className="px-3 py-1.5 text-right">
                              {a.auto_applied ? (
                                <Tag tone="violet" testId={`gap-${g.sku}-auto-${a.sku}`}>Auto {Math.round((a.confidence || 0) * 100)}%</Tag>
                              ) : a.learned ? (
                                <Tag tone="blue">Learned {Math.round((a.confidence || 0) * 100)}%</Tag>
                              ) : (
                                <span className="text-xs text-slate-400">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="What the engine has learned" testId="learned-pairs-panel" className="mt-4">
        {learned.length === 0 ? (
          <EmptyState
            title="Nothing learned yet"
            body="Answer a swap prompt above and the engine starts remembering. Pairs chosen 3+ times get promoted; 90%+ confidence pairs apply automatically."
            testId="learned-empty"
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">When low</th>
                <th className="px-3 py-2 text-left">Staff swap for</th>
                <th className="px-3 py-2 text-right">Chosen</th>
                <th className="px-3 py-2 text-right">Declined</th>
                <th className="px-3 py-2 text-right">Confidence</th>
                <th className="px-3 py-2 text-right">Learned ratio</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {learned.map((l) => (
                <tr key={`${l.product_id}-${l.alternative_id}`} data-testid={`learned-row-${l.sku}-${l.alternative_sku}`}>
                  <td className="mono px-4 py-2">{l.sku}</td>
                  <td className="mono px-3 py-2">{l.alternative_sku}</td>
                  <td className="num px-3 py-2 text-right">{num(l.accepted)}</td>
                  <td className="num px-3 py-2 text-right text-slate-500">{num(l.declined)}</td>
                  <td className="num px-3 py-2 text-right">{Math.round((l.confidence || 0) * 100)}%</td>
                  <td className="num px-3 py-2 text-right">{l.learned_ratio ?? "—"}</td>
                  <td className="px-3 py-2">
                    {l.auto_applied ? <Tag tone="violet">Auto-applied</Tag> : l.accepted >= 3 ? <Tag tone="blue">Promoted</Tag> : <Tag tone="slate">Learning</Tag>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel title="Gap history" testId="gap-history-panel" className="mt-4">
        {history.length === 0 ? (
          <EmptyState title="No gap changes recorded" body="Shortfall changes are logged here as stock moves." testId="gap-history-empty" />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">SKU</th>
                <th className="px-3 py-2 text-right">Previous</th>
                <th className="px-3 py-2 text-right">Now</th>
                <th className="px-3 py-2 text-left">Cause</th>
                <th className="px-3 py-2 text-left">When</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {history.map((h) => (
                <tr key={h.id}>
                  <td className="mono px-4 py-2">{h.sku}</td>
                  <td className="num px-3 py-2 text-right text-slate-500">{num(h.previous_shortfall)}</td>
                  <td className="num px-3 py-2 text-right">{num(h.shortfall)}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{causeLabel(h.cause)}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{shortDate(h.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </AppShell>
  );
}
